import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { FreelancerAutocomplete } from "@/components/freelancers/FreelancerAutocomplete";
import { ApprovalDateField } from "@/components/vagas/ApprovalDateField";
import { EncerrarDemaisField } from "@/components/vagas/EncerrarDemaisField";
import { isApprovalDateValid, todayLocalDate } from "@/components/vagas/approval-date";
import { formatCents, parseMoeda, simboloMoeda } from "@/utils/money";

interface AprovacaoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: {
    freelancer_id: string;
    valor_projeto: number;
    encerrar_demais: boolean;
    /** Dia da aprovação ("YYYY-MM-DD"), hoje ou antes. */
    data_aprovacao: string;
  }) => void;
  isLoading?: boolean;
  vagaId: string | null;
  freelancerSugeridoId?: string | null;
  freelancerSugeridoNome?: string | null;
  multiplosFreelancersAprovados?: boolean;
  verbaMinima?: number | null;
  verbaMaxima?: number | null;
  /** O projeto nasce na moeda da vaga. */
  moeda?: string | null;
}

export function AprovacaoModal({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  vagaId,
  freelancerSugeridoId,
  freelancerSugeridoNome,
  multiplosFreelancersAprovados = false,
  verbaMinima,
  verbaMaxima,
  moeda,
}: AprovacaoModalProps) {
  const [freelancerId, setFreelancerId] = useState<string>("");
  const [valor, setValor] = useState<string>("");
  const [encerrarDemais, setEncerrarDemais] = useState<boolean>(true);
  const [approvalDate, setApprovalDate] = useState<string>("");

  useEffect(() => {
    if (open) {
      setFreelancerId(freelancerSugeridoId ?? "");
      setValor("");
      setEncerrarDemais(true);
      setApprovalDate(todayLocalDate());
    }
  }, [open, freelancerSugeridoId]);

  const valorNumero = parseFloat(valor.replace(",", "."));
  const valorValido = !isNaN(valorNumero) && valorNumero > 0;
  const approvalDateValid = isApprovalDateValid(approvalDate);
  const isFormValid = !!freelancerId && valorValido && approvalDateValid;

  const moedaDaVaga = parseMoeda(moeda);
  const faixaMin = verbaMinima == null ? null : formatCents(verbaMinima, moedaDaVaga);
  const faixaMax = verbaMaxima == null ? null : formatCents(verbaMaxima, moedaDaVaga);
  const faixaTexto =
    faixaMin && faixaMax
      ? `Faixa da vaga: ${faixaMin} – ${faixaMax}`
      : faixaMin
      ? `Verba mínima da vaga: ${faixaMin}`
      : faixaMax
      ? `Verba máxima da vaga: ${faixaMax}`
      : null;

  const handleConfirm = () => {
    if (!isFormValid) return;
    onConfirm({
      freelancer_id: freelancerId,
      valor_projeto: valorNumero,
      encerrar_demais: encerrarDemais,
      data_aprovacao: approvalDate,
    });
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" />
            Aprovar Vaga
          </DialogTitle>
          <DialogDescription>
            Confirme o freelancer aprovado e o valor acordado. Será criado um
            projeto em rascunho vinculado à vaga.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!freelancerSugeridoId && (
            <Alert variant="default" className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-900">
                Nenhum freelancer está na etapa <strong>Aprovado</strong> do
                kanban desta vaga. Selecione manualmente abaixo.
              </AlertDescription>
            </Alert>
          )}
          {multiplosFreelancersAprovados && (
            <Alert variant="default" className="border-blue-300 bg-blue-50">
              <AlertTriangle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                Há mais de um freelancer na etapa Aprovado. Pré-selecionado o
                mais recente; confirme se é o correto.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="freelancer_aprovado">
              Freelancer <span className="text-red-500">*</span>
            </Label>
            <FreelancerAutocomplete
              value={freelancerId}
              onValueChange={(id) => setFreelancerId(id)}
              selectedLabel={
                freelancerId && freelancerId === freelancerSugeridoId
                  ? freelancerSugeridoNome ?? undefined
                  : undefined
              }
              placeholder="Selecione o freelancer aprovado"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="valor_projeto">
              Valor do Projeto ({simboloMoeda(moedaDaVaga)}) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="valor_projeto"
              type="number"
              step="0.01"
              min="0"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="Ex: 5000.00"
              disabled={isLoading}
            />
            {faixaTexto && (
              <p className="text-xs text-muted-foreground">{faixaTexto}</p>
            )}
          </div>

          <ApprovalDateField value={approvalDate} onChange={setApprovalDate} disabled={isLoading} />

          <EncerrarDemaisField
            id="encerrar_demais"
            vagaId={vagaId}
            freelancerAprovadoId={freelancerId || null}
            checked={encerrarDemais}
            onCheckedChange={setEncerrarDemais}
            disabled={isLoading}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isLoading ? "Aprovando..." : "Confirmar Aprovação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
